import { Alert, Button, Group, Modal, NumberInput, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { moneyValue } from "../../lib/money-input";

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Refund all or part of a payment (ADR 0009): a registration's or a store order's. The money goes back to the card
 * it came from, from the church's own Stripe balance. A reason is kept on the refund in Stripe and counted, never
 * quoted, in the audit row. Shared by the event roster and the store's orders.
 */
export function RefundDialog({
  title,
  payer,
  amountCents,
  refundedCents,
  currency,
  pending,
  error,
  onRefund,
  onClose,
}: {
  readonly title: string;
  readonly payer: string;
  readonly amountCents: number;
  readonly refundedCents: number;
  readonly currency: string;
  readonly pending: boolean;
  readonly error: Error | null;
  readonly onRefund: (amountCents: number, reason: string) => void;
  readonly onClose: () => void;
}) {
  const remaining = amountCents - refundedCents;
  // An emptied field stays empty while the person types; see moneyValue for what the input reports.
  const [dollars, setDollars] = useState<number | "">(remaining / 100);
  const [reason, setReason] = useState("");
  const cents = dollars === "" ? 0 : Math.round(dollars * 100);
  const valid = cents > 0 && cents <= remaining;

  return (
    <Modal opened onClose={onClose} title={title}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {payer} paid {money(amountCents, currency)}
          {refundedCents > 0
            ? `, of which ${money(refundedCents, currency)} is already refunded`
            : ""}
          . The refund goes back to the card it came from, from the church's Stripe balance, and
          usually shows within five to ten days. Stripe's processing fee is not returned.
        </Text>
        <NumberInput
          label="Amount to refund"
          prefix="$"
          decimalScale={2}
          fixedDecimalScale
          min={0.01}
          max={remaining / 100}
          value={dollars}
          onChange={(value) => {
            setDollars(moneyValue(value));
          }}
          maw={220}
        />
        <TextInput
          label="Reason"
          description="Kept with the refund in Stripe. Optional."
          maxLength={200}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
          }}
        />
        {error ? (
          <Alert color="red" variant="light">
            {error.message}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Keep the payment
          </Button>
          <Button
            color="red"
            loading={pending}
            disabled={!valid}
            onClick={() => {
              onRefund(cents, reason);
            }}
          >
            Refund {money(cents, currency)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
